import { PostgrestClient } from "@supabase/postgrest-js";
import type { DatabaseWithOptions } from "../../../__definitions__/database-definitions";
import type { UUID } from "../../../utils/uuid";
import { ACCOUNT_LIMIT_KEYS, type AccountLimitKey } from "../server/supabase-account-service";

export type AccountLimitTypeMap = {
  [K in keyof typeof ACCOUNT_LIMIT_KEYS]: (typeof ACCOUNT_LIMIT_KEYS)[K] extends { type: "number" }
    ? number
    : (typeof ACCOUNT_LIMIT_KEYS)[K] extends { type: "boolean" }
      ? boolean
      : (typeof ACCOUNT_LIMIT_KEYS)[K] extends { type: "select"; values: readonly (infer V)[] }
        ? V
        : never;
};

function parseValue<K extends AccountLimitKey>(key: K, raw: number): AccountLimitTypeMap[K] {
  if (ACCOUNT_LIMIT_KEYS[key].type === "boolean") {
    return Boolean(raw) as AccountLimitTypeMap[K];
  }

  if (ACCOUNT_LIMIT_KEYS[key].type === "number") {
    return raw as AccountLimitTypeMap[K];
  }

  // Select keys need explicit mapping to their options.
  if (ACCOUNT_LIMIT_KEYS[key].type === "select") {
    return ACCOUNT_LIMIT_KEYS[key].values[raw] as AccountLimitTypeMap[K];
  }
  throw new Error(`Unsupported account limit key type for key: ${key}`);
}

export async function getAllAccountLimits(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"]>,
  accountId: UUID,
): Promise<AccountLimitTypeMap> {
  const { data, error } = await supabase
    .from("account_limit")
    .select("key, count")
    .eq("account_id", accountId)
    .in("key", Object.keys(ACCOUNT_LIMIT_KEYS));

  if (error) throw new Error(error.message);

  const limits = data.reduce(
    (acc, curr) => {
      acc[curr.key] = curr.count;
      return acc;
    },
    {} as { [key: string]: number },
  );

  return {
    slack_aliases: parseValue("slack_aliases", limits["slack_aliases"] || 0),
    super_admins: parseValue("super_admins", limits["super_admins"] || 0),
    contributors: parseValue("contributors", limits["contributors"] || 0),
    unlayer_fallback: parseValue("unlayer_fallback", limits["unlayer_fallback"] || 0),
    omit_email_test_indicator: parseValue(
      "omit_email_test_indicator",
      limits["omit_email_test_indicator"] || 0,
    ),
    email_design_templates: parseValue(
      "email_design_templates",
      limits["email_design_templates"] || 0,
    ),
    allow_all_and_upcoming_segment: parseValue(
      "allow_all_and_upcoming_segment",
      limits["allow_all_and_upcoming_segment"] || 0,
    ),
    slack_connections: parseValue("slack_connections", limits["slack_connections"] || 0),
    enable_sms_two_way: parseValue("enable_sms_two_way", limits["enable_sms_two_way"] || 0),
    enable_swag: parseValue("enable_swag", limits["enable_swag"] || 0),
    enable_e_gifting: parseValue("enable_e_gifting", limits["enable_e_gifting"] || 0),
    enable_videography: parseValue("enable_videography", limits["enable_videography"] || 0),
    enable_design_services: parseValue(
      "enable_design_services",
      limits["enable_design_services"] || 0,
    ),
    enable_listen: parseValue("enable_listen", limits["enable_listen"] || 0),
    enable_survey_ai_analysis: parseValue(
      "enable_survey_ai_analysis",
      limits["enable_survey_ai_analysis"] || 0,
    ),
    design_services: parseValue("design_services", limits["design_services"] || 0),
    distribution_credit: parseValue("distribution_credit", limits["distribution_credit"] || 0),
    listen_entitlement: parseValue("listen_entitlement", limits["listen_entitlement"] || 0),
    enable_polls: parseValue("enable_polls", limits["enable_polls"] || 0),
    enable_knowledge_hub: parseValue("enable_knowledge_hub", limits["enable_knowledge_hub"] || 0),
    hide_additional_services_upsell: parseValue(
      "hide_additional_services_upsell",
      limits["hide_additional_services_upsell"] || 0,
    ),
    enable_pii_guidance: parseValue("enable_pii_guidance", limits["enable_pii_guidance"] || 0),
    communication_insights: parseValue(
      "communication_insights",
      limits["communication_insights"] || 0,
    ),
    enable_advanced_email_headers: parseValue(
      "enable_advanced_email_headers",
      limits["enable_advanced_email_headers"] || 0,
    ),
  };
}
